import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eiendomAPI } from '../services/api';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const { takstmann, logout } = useAuth();
  const [eiendommer, setEiendommer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNyEiendom, setShowNyEiendom] = useState(false);
  const [formData, setFormData] = useState({
    adresse: '',
    postnr: '',
    poststed: '',
    kommune: '',
    gnr: '',
    bnr: '',
    fnr: '',
    snr: '',
    eier_navn: '',
    eier_telefon: '',
    eier_epost: '',
  });

  useEffect(() => {
    fetchEiendommer();
  }, []);

  const fetchEiendommer = async () => {
    try {
      const response = await eiendomAPI.getAll();
      setEiendommer(response.data);
    } catch (err) {
      setError('Kunne ikke hente eiendommer');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Nullstill feilmelding

    try {
      const data = {
        ...formData,
        gnr: formData.gnr ? parseInt(formData.gnr) : null,
        bnr: formData.bnr ? parseInt(formData.bnr) : null,
        fnr: formData.fnr ? parseInt(formData.fnr) : null,
        snr: formData.snr ? parseInt(formData.snr) : null,
      };

      const response = await eiendomAPI.create(data);
      const nyEiendomId = response.data.id;

      setError(''); // Nullstill feilmelding
      setShowNyEiendom(false);
      setFormData({
        adresse: '',
        postnr: '',
        poststed: '',
        kommune: '',
        gnr: '',
        bnr: '',
        fnr: '',
        snr: '',
        eier_navn: '',
        eier_telefon: '',
        eier_epost: '',
      });

      // Naviger direkte til den nye eiendommen
      navigate(`/eiendom/${nyEiendomId}`);
    } catch (err) {
      setError('Kunne ikke opprette eiendom: ' + (err.response?.data?.error || err.message));
      console.error(err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const statusTekst = (status) => {
    const statuser = {
      utkast: 'Utkast',
      under_befaring: 'Under befaring',
      ferdig: 'Ferdig',
      arkivert: 'Arkivert',
    };
    return statuser[status] || status;
  };

  return (
    <div className="dashboard">
      <nav className="navbar">
        <div className="navbar-content">
          <h1>Takstmann System</h1>
          <div className="navbar-right">
            <span className="user-name">{takstmann?.navn}</span>
            <button onClick={handleLogout} className="btn-logout">
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h2>Mine eiendommer</h2>
          <button
            onClick={() => setShowNyEiendom(!showNyEiendom)}
            className="btn-primary"
          >
            + Ny eiendom
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showNyEiendom && (
          <div className="ny-eiendom-form">
            <h3>Opprett ny eiendom</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Adresse *</label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Postnr</label>
                  <input
                    type="text"
                    name="postnr"
                    value={formData.postnr}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Poststed</label>
                  <input
                    type="text"
                    name="poststed"
                    value={formData.poststed}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kommune *</label>
                  <input
                    type="text"
                    name="kommune"
                    value={formData.kommune}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Gnr</label>
                  <input
                    type="number"
                    name="gnr"
                    value={formData.gnr}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Bnr</label>
                  <input
                    type="number"
                    name="bnr"
                    value={formData.bnr}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Eier navn</label>
                  <input
                    type="text"
                    name="eier_navn"
                    value={formData.eier_navn}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Eier telefon</label>
                  <input
                    type="text"
                    name="eier_telefon"
                    value={formData.eier_telefon}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Eier e-post</label>
                  <input
                    type="email"
                    name="eier_epost"
                    value={formData.eier_epost}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Opprett
                </button>
                <button
                  type="button"
                  onClick={() => setShowNyEiendom(false)}
                  className="btn-secondary"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Laster eiendommer...</div>
        ) : eiendommer.length === 0 ? (
          <div className="empty-state">
            <p>Ingen eiendommer ennå</p>
            <p>Klikk "Ny eiendom" for å komme i gang</p>
          </div>
        ) : (
          <div className="eiendommer-grid">
            {eiendommer.map((eiendom) => (
              <div
                key={eiendom.id}
                className="eiendom-card"
                onClick={() => navigate(`/eiendom/${eiendom.id}`)}
              >
                <h3>{eiendom.adresse}</h3>
                <p className="eiendom-info">
                  {eiendom.postnr} {eiendom.poststed}
                </p>
                {eiendom.gnr && eiendom.bnr && (
                  <p className="eiendom-info">
                    Gnr {eiendom.gnr}, bnr {eiendom.bnr}
                  </p>
                )}
                {eiendom.eier_navn && (
                  <p className="eiendom-info">Eier: {eiendom.eier_navn}</p>
                )}
                <div className="eiendom-footer">
                  <span className={`status status-${eiendom.status}`}>
                    {statusTekst(eiendom.status)}
                  </span>
                  <span className="eiendom-dato">
                    {new Date(eiendom.created_at).toLocaleDateString('nb-NO')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
